import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { getTodayDateString, formatDateInBusinessTZ } from '../utils/date';
import { trackEvent } from '../utils/analytics';
import { DEFAULT_BHCS_WAIVER_SIGNATURE_FONT_STACK } from '../types/waiver';
import type { BhcsStudentWaiver, ContactInfo, PhysicianInfo, InsuranceInfo } from '../types/waiver';

interface StudentLite {
  id: string;
  first_name: string;
  last_name: string;
  active: boolean;
  metadata?: Record<string, unknown> | null;
}

export default function StudentWaiver() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch parent by email
  const { data: parentData } = useQuery({
    queryKey: ['parent-by-email', user?.email],
    enabled: !!user?.email,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email, phone')
        .eq('email', user?.email as string)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; first_name: string; last_name: string; email: string; phone: string } | null;
    }
  });

  // Fetch students for this parent
  const { data: students } = useQuery<StudentLite[]>({
    queryKey: ['parent-students-lite', parentData?.id],
    enabled: !!parentData?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_parents')
        .select('students (id, first_name, last_name, active, metadata)')
        .eq('parent_id', parentData?.id as string);
      if (error) throw error;
      const rows = (data || []) as Array<{ students: StudentLite | StudentLite[] }>;
      const flattened: StudentLite[] = [];
      for (const r of rows) {
        const value = r.students;
        if (Array.isArray(value)) {
          for (const s of value) {
            if (s?.id) flattened.push(s);
          }
        } else if (value && value.id) {
          flattened.push(value);
        }
      }
      return flattened;
    }
  });

  // Determine which students still need a waiver
  interface StudentMetadataShape {
    bhcs_waiver?: BhcsStudentWaiver;
    [key: string]: unknown;
  }
  const hasSignedWaiver = (student: StudentLite): boolean => {
    const md = (student.metadata as StudentMetadataShape | undefined) || undefined;
    return !!md?.bhcs_waiver;
  };
  const eligibleStudents = useMemo(() => {
    return (students || []).filter(s => s.active && !hasSignedWaiver(s));
  }, [students]);

  const defaultSelectedIds = useMemo(() => {
    return eligibleStudents.map(s => s.id);
  }, [eligibleStudents]);

  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  useEffect(() => {
    setSelectedStudentIds(defaultSelectedIds);
  }, [defaultSelectedIds.join('|')]); // reselect when eligible students change
  // Track waiver page view once on mount when auth is available
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (user && !hasTrackedViewRef.current) {
      trackEvent({
        eventType: 'waiver_view',
        user,
        userType: 'parent',
        entityType: 'waiver',
        metadata: {
          eligible_student_count: eligibleStudents.length
        }
      });
      hasTrackedViewRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Form state
  const [parent1, setParent1] = useState<ContactInfo>({
    name: '',
    relationship: '',
    phone: '',
    email: user?.email ?? ''
  });
  const [parent2, setParent2] = useState<ContactInfo>({
    name: '',
    relationship: '',
    phone: '',
    email: ''
  });
  const [emergency, setEmergency] = useState<ContactInfo>({
    name: '',
    relationship: '',
    phone: '',
    email: ''
  });
  const [hasAllergies, setHasAllergies] = useState<boolean>(false);
  const [allergiesDetails, setAllergiesDetails] = useState<string>('');
  const [physician, setPhysician] = useState<PhysicianInfo>({ name: '', phone: '', address: '' });
  const [insurance, setInsurance] = useState<InsuranceInfo>({ provider: '', policyNumber: '', groupNumber: '' });
  const [consents, setConsents] = useState({
    emergencyMedicalAuthorization: false,
    liabilityWaiver: false,
    assumptionOfRisk: false,
    behaviorPolicy: false,
    infectiousDiseaseDisclaimer: false,
    photoRelease: false,
  });
  const [signaturePrintedName, setSignaturePrintedName] = useState<string>('');
  const [signature, setSignature] = useState<string>('');
  const [signatureDate, setSignatureDate] = useState<string>(getTodayDateString());

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');

  // Signature font selection (hover/tap to choose)
  const fontOptions = [
    { label: 'Snell Roundhand', stack: DEFAULT_BHCS_WAIVER_SIGNATURE_FONT_STACK },
    { label: 'Apple Chancery', stack: '"Apple Chancery", cursive' },
    { label: 'Segoe Script', stack: '"Segoe Script", cursive' },
    { label: 'Lucida Handwriting', stack: '"Lucida Handwriting", cursive' },
    { label: 'Brush Script MT', stack: '"Brush Script MT", cursive' },
    { label: 'Bradley Hand', stack: '"Bradley Hand", cursive' },
    { label: 'Cursive (default)', stack: 'cursive' },
  ];
  const [signatureFont, setSignatureFont] = useState<string>(fontOptions[0].stack);
  const [showFontPicker, setShowFontPicker] = useState<boolean>(false);
  const fontPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showFontPicker) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      if (fontPickerRef.current && target && !fontPickerRef.current.contains(target)) {
        setShowFontPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFontPicker]);

  const toggleSelectedStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };

  const allConsentChecked = consents.emergencyMedicalAuthorization &&
    consents.liabilityWaiver &&
    consents.assumptionOfRisk &&
    consents.behaviorPolicy &&
    consents.infectiousDiseaseDisclaimer &&
    consents.photoRelease;

  const validate = (): string | null => {
    if (!selectedStudentIds.length) return 'Please select at least one student.';
    if (!parent1.name || !parent1.relationship || !parent1.phone || !parent1.email) {
      return 'Please complete Parent/Guardian 1 contact information.';
    }
    if (!emergency.name || !emergency.relationship || !emergency.phone || !emergency.email) {
      return 'Please complete the Emergency Contact information.';
    }
    if (hasAllergies && !allergiesDetails.trim()) {
      return 'Please provide allergy/medical details.';
    }
    if (!allConsentChecked) {
      return 'Please review and accept all required authorizations.';
    }
    if (!signaturePrintedName || !signature || !signatureDate) {
      return 'Please provide signature, printed name, and date.';
    }
    return null;
  };

  const { mutate: submitWaiver, isPending } = useMutation({
    mutationFn: async () => {
      const validationError = validate();
      if (validationError) {
        throw new Error(validationError);
      }
      if (!user?.email) {
        throw new Error('Not authenticated');
      }
      // Build waiver payload
      const payload: BhcsStudentWaiver = {
        parent1,
        parent2: parent2.name ? parent2 : undefined,
        emergencyContact: emergency,
        hasAllergies,
        allergiesDetails: hasAllergies ? allergiesDetails : undefined,
        physician: physician.name ? physician : undefined,
        insurance: insurance.provider ? insurance : undefined,
        consents: {
          emergencyMedicalAuthorization: consents.emergencyMedicalAuthorization,
          liabilityWaiver: consents.liabilityWaiver,
          assumptionOfRisk: consents.assumptionOfRisk,
          behaviorPolicy: consents.behaviorPolicy,
          photoRelease: consents.photoRelease,
          infectiousDiseaseDisclaimer: consents.infectiousDiseaseDisclaimer,
        },
        signature: {
          printedName: signaturePrintedName,
          signature,
          date: signatureDate,
          fontStack: signatureFont,
        },
        submittedAt: new Date().toISOString(),
      };

      // Update each selected student's metadata.bhcs_waiver
      // We read current metadata from the cached students query when available
      const currentStudents = students || [];

      for (const studentId of selectedStudentIds) {
        const student = currentStudents.find(s => s.id === studentId);
        const existingMetadata = (student?.metadata as Record<string, unknown> | undefined) || {};
        const newMetadata = { ...existingMetadata, bhcs_waiver: payload };
        const { error } = await supabase
          .from('students')
          .update({ metadata: newMetadata })
          .eq('id', studentId);
        if (error) throw error;
      }
      return true;
    },
    onSuccess: async () => {
      setSuccessMsg('Waiver submitted successfully.');
      setErrorMsg('');
      if (user) {
        trackEvent({
          eventType: 'waiver_submit',
          user,
          userType: 'parent',
          entityType: 'waiver',
          metadata: {
            student_ids: selectedStudentIds,
            consents: {
              emergencyMedicalAuthorization: consents.emergencyMedicalAuthorization,
              liabilityWaiver: consents.liabilityWaiver,
              assumptionOfRisk: consents.assumptionOfRisk,
              behaviorPolicy: consents.behaviorPolicy,
              photoRelease: consents.photoRelease,
              infectiousDiseaseDisclaimer: consents.infectiousDiseaseDisclaimer,
            }
          }
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['parent-students-lite'] });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Submission failed';
      setErrorMsg(message);
      setSuccessMsg('');
    }
  });

  const studentNameList = useMemo(() => {
    const selected = (eligibleStudents || []).filter(s => selectedStudentIds.includes(s.id));
    return selected.map(s => `${s.first_name} ${s.last_name}`).join(', ');
  }, [eligibleStudents, selectedStudentIds]);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-800">Bright Minds Collective LLC</p>
        <p className="text-sm text-gray-600">Operator of Bright Horizon Chinese School and Kaleido</p>
        <p className="text-sm text-gray-600">650 Shell Blvd, Foster City, CA 94404</p>
        <p className="text-sm text-gray-600">(650) 620 - 0299</p>
        <p className="text-sm text-gray-600">Info@bhcskids.com</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>STUDENT PARTICIPATION WAIVER & RELEASE FORM</CardTitle>
          <CardDescription>
            This form applies to all student participation in programs, activities, and events organized by Bright Minds Collective LLC through its Bright Horizon Chinese School and Kaleido programs, including but not limited to weekend classes, after-school programs, extracurricular activities, seasonal camps (e.g., summer, spring, and winter camp), and early-learning workshops, series, and community events.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Students selection */}
          <section>
            <h3 className="font-semibold mb-3">Select Student(s)</h3>
            <p className="text-sm text-gray-600 mb-3">Choose which child(ren) this waiver applies to.</p>
            <div className="space-y-2">
              {(eligibleStudents || []).map(s => (
                <label key={s.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedStudentIds.includes(s.id)}
                    onChange={() => toggleSelectedStudent(s.id)}
                  />
                  <span>{s.first_name} {s.last_name}</span>
                </label>
              ))}
              {eligibleStudents.length === 0 && (
                <p className="text-sm text-gray-500">
                  All your students have a signed waiver. There are no remaining students to sign.
                </p>
              )}
            </div>
          </section>

          {/* Parent 1 */}
          <section>
            <h3 className="font-semibold mb-3">Parent 1/Guardian 1 Contact Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p1-name">Name</Label>
                <Input id="p1-name" value={parent1.name} onChange={e => setParent1({ ...parent1, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="p1-relationship">Relationship to Student</Label>
                <Input id="p1-relationship" value={parent1.relationship} onChange={e => setParent1({ ...parent1, relationship: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="p1-phone">Phone Number</Label>
                <Input id="p1-phone" value={parent1.phone} onChange={e => setParent1({ ...parent1, phone: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="p1-email">Email Address</Label>
                <Input id="p1-email" type="email" value={parent1.email} onChange={e => setParent1({ ...parent1, email: e.target.value })} required />
              </div>
            </div>
          </section>

          {/* Parent 2 */}
          <section>
            <h3 className="font-semibold mb-3">Parent 2/Guardian 2 Contact Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p2-name">Name (optional)</Label>
                <Input id="p2-name" value={parent2.name} onChange={e => setParent2({ ...parent2, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="p2-relationship">Relationship to Student (optional)</Label>
                <Input id="p2-relationship" value={parent2.relationship} onChange={e => setParent2({ ...parent2, relationship: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="p2-phone">Phone Number (optional)</Label>
                <Input id="p2-phone" value={parent2.phone} onChange={e => setParent2({ ...parent2, phone: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="p2-email">Email Address (optional)</Label>
                <Input id="p2-email" type="email" value={parent2.email} onChange={e => setParent2({ ...parent2, email: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          <section>
            <h3 className="font-semibold mb-3">Emergency Contact Information (Other Than Parent)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="em-name">Name</Label>
                <Input id="em-name" value={emergency.name} onChange={e => setEmergency({ ...emergency, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="em-relationship">Relationship to Student</Label>
                <Input id="em-relationship" value={emergency.relationship} onChange={e => setEmergency({ ...emergency, relationship: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="em-phone">Phone Number</Label>
                <Input id="em-phone" value={emergency.phone} onChange={e => setEmergency({ ...emergency, phone: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="em-email">Email Address</Label>
                <Input id="em-email" type="email" value={emergency.email} onChange={e => setEmergency({ ...emergency, email: e.target.value })} required />
              </div>
            </div>
          </section>

          {/* Allergies & Medical Conditions */}
          <section>
            <h3 className="font-semibold mb-3">Allergies & Medical Conditions</h3>
            <p className="text-sm text-gray-600 mb-2">Does your child have any food allergies or other medical conditions we should be aware of?</p>
            <div className="flex items-center gap-6 mb-3">
              <label className="flex items-center gap-2">
                <input type="radio" name="allergies" checked={!hasAllergies} onChange={() => setHasAllergies(false)} />
                <span>No</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="allergies" checked={hasAllergies} onChange={() => setHasAllergies(true)} />
                <span>Yes</span>
              </label>
            </div>
            {hasAllergies && (
              <div>
                <Label htmlFor="allergy-details">Please list and explain (If you enrolled more than one child, please specify which child has the allergy.)</Label>
                <textarea
                  id="allergy-details"
                  className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={4}
                  value={allergiesDetails}
                  onChange={e => setAllergiesDetails(e.target.value)}
                />
              </div>
            )}
          </section>

          {/* Primary Care Physician */}
          <section>
            <h3 className="font-semibold mb-3">Primary Care Physician</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="physician-name">Physician's Name (optional)</Label>
                <Input id="physician-name" value={physician.name} onChange={e => setPhysician({ ...physician, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="physician-phone">Phone Number (optional)</Label>
                <Input id="physician-phone" value={physician.phone} onChange={e => setPhysician({ ...physician, phone: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="physician-address">Address (optional)</Label>
                <Input id="physician-address" value={physician.address} onChange={e => setPhysician({ ...physician, address: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Health Insurance Information */}
          <section>
            <h3 className="font-semibold mb-3">Health Insurance Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ins-provider">Insurance Provider (optional)</Label>
                <Input id="ins-provider" value={insurance.provider} onChange={e => setInsurance({ ...insurance, provider: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ins-policy">Policy Number / Member ID (optional)</Label>
                <Input id="ins-policy" value={insurance.policyNumber} onChange={e => setInsurance({ ...insurance, policyNumber: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ins-group">Group Number (optional)</Label>
                <Input id="ins-group" value={insurance.groupNumber} onChange={e => setInsurance({ ...insurance, groupNumber: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Field Trip Permission */}
          <section>
            <h3 className="font-semibold mb-3">Field Trip & Off-Site Activity Permission & Release</h3>
            <p className="text-sm text-gray-700 mb-2">
              I give permission for my child(ren), <span className="font-medium">{studentNameList || '____________________________'}</span>, to participate in any field trips, outdoor activities, or off-site excursions organized by Bright Minds Collective LLC during the school year afterschool programs, seasonal programs, or Kaleido programs. I release and discharge the staff, teachers, owners, and volunteers of Bright Minds Collective LLC from all liabilities arising out of or in connection with such activities.
            </p>
            <p className="text-sm text-gray-700">
              Liability includes all claims, demands, losses, causes of action, suits, or judgments of any and every kind that I, my heirs, executors, administrators, or assignees may have against Bright Minds Collective LLC, including any resulting from injury, illness, property damage, or death occurring during such activities.
            </p>
          </section>

          {/* Authorizations & Consents */}
          <section>
            <h3 className="font-semibold mb-3">Authorization & Consent</h3>
            <div className="space-y-3">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.emergencyMedicalAuthorization}
                  onChange={e => setConsents(v => ({ ...v, emergencyMedicalAuthorization: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Emergency Medical Authorization:</span> In the event of an emergency, I authorize Bright Minds Collective LLC staff to take appropriate action for the safety and well-being of my child, including seeking emergency medical treatment and arranging emergency transportation if necessary. I understand that every effort will be made to contact me. If my child’s physician cannot be reached, I consent to care from qualified emergency medical personnel, including hospital staff and licensed physicians. I accept responsibility for any and all costs incurred in obtaining such care.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.liabilityWaiver}
                  onChange={e => setConsents(v => ({ ...v, liabilityWaiver: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Liability Waiver:</span> I release and hold harmless Bright Minds Collective LLC, its directors, staff, and volunteers from any and all liability for injuries, illnesses, or damages sustained by my child during school-related and other program activities, including on-site instruction, off-site events, and transportation.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.assumptionOfRisk}
                  onChange={e => setConsents(v => ({ ...v, assumptionOfRisk: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Assumption of Risk:</span> I understand that participation in school, extracurricular, and early-learning activities involves inherent risks. I assume full responsibility for these risks on behalf of my child.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.behaviorPolicy}
                  onChange={e => setConsents(v => ({ ...v, behaviorPolicy: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Behavior Policy:</span> I acknowledge that my child is expected to follow the rules and behavioral guidelines of Bright Minds Collective LLC and its programs. Bright Minds Collective LLC may suspend or dismiss any student for unsafe or inappropriate behavior without refund.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.photoRelease}
                  onChange={e => setConsents(v => ({ ...v, photoRelease: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Photo Release:</span> I acknowledge and agree that Bright Minds Collective LLC may take photographs and videos during lessons, activities, camps, and events for the purpose of marketing and promotional use.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={consents.infectiousDiseaseDisclaimer}
                  onChange={e => setConsents(v => ({ ...v, infectiousDiseaseDisclaimer: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Infectious Disease Disclaimer:</span> I understand that despite reasonable safety precautions, exposure to infectious diseases (including COVID-19) may still occur. I agree not to hold Bright Minds Collective LLC responsible in such an event.
                </span>
              </label>
            </div>
          </section>

          {/* Signature */}
          <section>
            <h3 className="font-semibold mb-3">Signature</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="sig-printed">Parent/Guardian Name (Printed)</Label>
                <Input id="sig-printed" value={signaturePrintedName} onChange={e => setSignaturePrintedName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="sig-signature">Signature</Label>
                <div className="relative" ref={fontPickerRef}>
                  <div className="flex items-center gap-2">
                    <Input
                      id="sig-signature"
                      value={signature}
                      onChange={e => setSignature(e.target.value)}
                      className=""
                      style={{ fontFamily: signatureFont, fontSize: '1.25rem' }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setShowFontPicker(prev => !prev)}
                    >
                      Choose style
                    </Button>
                  </div>
                  {showFontPicker && (
                    <div className="absolute z-10 mt-2 w-56 rounded-md border bg-white shadow-lg p-2">
                      <div className="text-xs text-gray-500 px-2 pb-1">Choose signature style</div>
                      <ul className="max-h-56 overflow-auto">
                        {fontOptions.map((opt) => (
                          <li key={opt.label}>
                            <button
                              type="button"
                              className="w-full text-left px-2 py-1 rounded hover:bg-gray-100"
                              onClick={() => {
                                setSignatureFont(opt.stack);
                                setShowFontPicker(false);
                              }}
                              style={{ fontFamily: opt.stack }}
                              title={opt.label}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="sig-date">Date</Label>
                <Input id="sig-date" type="date" value={signatureDate} onChange={e => setSignatureDate(e.target.value)} />
                <p className="text-xs text-gray-500 mt-1">Today: {formatDateInBusinessTZ(new Date())}</p>
              </div>
            </div>
          </section>

          {/* Status */}
          {(errorMsg || successMsg) && (
            <div className={successMsg ? 'text-green-700 text-sm' : 'text-red-700 text-sm'}>
              {successMsg || errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => submitWaiver()}
              disabled={isPending || !user || eligibleStudents.length === 0}
            >
              {isPending ? 'Submitting...' : 'Submit Waiver'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


